import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import ReportStatCard from './ReportStatCard';
import { projectService } from '../../../services/projectService';

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444'];

const DeliverableTrackingTab = ({ dateRange }) => {
  const [projects, setProjects] = useState([]);
  const [allDeliverables, setAllDeliverables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: projectData, error: projError } = await projectService?.getAll();
      if (projError) throw projError;

      const projectList = projectData || [];
      setProjects(projectList);

      // Fetch deliverables for all projects
      const deliverablePromises = projectList?.slice(0, 10)?.map(p =>
        projectService?.getDeliverables(p?.id)?.catch(() => ({ data: [] }))
      );
      const results = await Promise.all(deliverablePromises || []);
      const combined = results?.flatMap(r => r?.data || []);
      setAllDeliverables(combined);
    } catch (err) {
      setError(err?.message || 'Failed to load deliverable data');
    } finally {
      setLoading(false);
    }
  };

  const totalDeliverables = allDeliverables?.length;
  const completedDeliverables = allDeliverables?.filter(d => d?.status === 'completed')?.length;
  const inProgressDeliverables = allDeliverables?.filter(d => d?.status === 'in-progress')?.length;
  const blockedDeliverables = allDeliverables?.filter(d => d?.status === 'blocked')?.length;
  const completionRate = totalDeliverables > 0 ? Math.round((completedDeliverables / totalDeliverables) * 100) : 0;

  const statusData = [
    { name: 'Completed', value: completedDeliverables },
    { name: 'In Progress', value: inProgressDeliverables },
    { name: 'Pending', value: allDeliverables?.filter(d => d?.status === 'pending')?.length },
    { name: 'Blocked', value: blockedDeliverables }
  ]?.filter(d => d?.value > 0);

  const priorityData = ['critical', 'high', 'medium', 'low']?.map(priority => ({
    name: priority?.charAt(0)?.toUpperCase() + priority?.slice(1),
    total: allDeliverables?.filter(d => d?.priority === priority)?.length,
    completed: allDeliverables?.filter(d => d?.priority === priority && d?.status === 'completed')?.length
  }))?.filter(d => d?.total > 0);

  const projectDeliverableData = projects?.slice(0, 8)?.map(p => {
    const projectDeliverables = allDeliverables?.filter(d => d?.projectId === p?.id);
    return {
      name: (p?.projectName || p?.name || '')?.substring(0, 12) + ((p?.projectName || p?.name || '')?.length > 12 ? '...' : ''),
      total: projectDeliverables?.length,
      completed: projectDeliverables?.filter(d => d?.status === 'completed')?.length
    };
  })?.filter(d => d?.total > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <Icon name="AlertCircle" size={20} className="inline mr-2" />{error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <ReportStatCard title="Total Deliverables" value={totalDeliverables} icon="ListTodo" color="accent" subtitle="" trend={0} trendLabel="" />
        <ReportStatCard title="Completed" value={completedDeliverables} icon="CheckSquare" color="green" subtitle="" trend={0} trendLabel="" />
        <ReportStatCard title="Completion Rate" value={`${completionRate}%`} icon="TrendingUp" color="blue" subtitle="" trend={0} trendLabel="" />
        <ReportStatCard title="Blocked" value={blockedDeliverables} icon="AlertOctagon" color="red" subtitle="Needs attention" trend={0} trendLabel="" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-foreground mb-4">Deliverables by Status</h3>
          {statusData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" outerRadius={90} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}>
                  {statusData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-60 text-muted-foreground text-sm">No deliverable data available</div>
          )}
        </div>

        <div className="bg-card rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-foreground mb-4">Completion by Priority</h3>
          {priorityData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={priorityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="total" name="Total" fill="#94A3B8" radius={[4,4,0,0]} />
                <Bar dataKey="completed" name="Completed" fill="#10B981" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-60 text-muted-foreground text-sm">No data available</div>
          )}
        </div>
      </div>
      {projectDeliverableData?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-foreground mb-4">Deliverables by Project</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={projectDeliverableData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="total" name="Total" fill="#3B82F6" radius={[4,4,0,0]} />
              <Bar dataKey="completed" name="Completed" fill="#10B981" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-5 border-b border-border">
          <h3 className="text-base font-semibold text-foreground">Recent Deliverables</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Deliverable</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Priority</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Status</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Due Date</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Est. Hours</th>
              </tr>
            </thead>
            <tbody>
              {allDeliverables?.slice(0, 12)?.map(d => (
                <tr key={d?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                  <td className="px-5 py-3">
                    <p className="text-sm font-medium text-foreground">{d?.name}</p>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      d?.priority === 'critical' ? 'bg-red-100 text-red-700' :
                      d?.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                      d?.priority === 'medium'? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                    }`}>{d?.priority}</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      d?.status === 'completed' ? 'bg-green-100 text-green-700' :
                      d?.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                      d?.status === 'blocked'? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                    }`}>{d?.status}</span>
                  </td>
                  <td className="px-5 py-3 text-sm text-muted-foreground">{d?.endDate || '—'}</td>
                  <td className="px-5 py-3 text-sm text-right text-foreground">{d?.estimatedHours || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {allDeliverables?.length === 0 && (
            <div className="p-8 text-center text-muted-foreground text-sm">No deliverables found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliverableTrackingTab;